import { Category } from "@domain/models/Category";
import { type GQL, Model } from "@binale-tech/shared";
import { type BuTaxDB, type BuTimeframe } from "@domain/models/Bu";

/**
 * SKR-aware helpers for resolving BU/tax data over time. Lives in the
 * frontend because the SPA loads the entire SKR catalogue
 * (`buTimeframes`) into memory at boot and the methods here walk that
 * in-memory list. The backend pulls single Firestore rows per request
 * and never assembles a timeframe-shaped view.
 *
 * The per-row pure helpers `isTaxOnTop` and `hasExtraTaxToCategory` live
 * in `@binale-tech/shared` (`Model.isTaxOnTop`, `Model.hasExtraTaxToCategory`)
 * because both sides need them. Re-exported here under the legacy
 * `BuTaxesSKR.*` names for backward compatibility with the existing
 * call sites.
 */
export class BuTaxesSKR {
    protected static readonly TAX_BU_KU_DB: BuTaxDB = {
        bu: Model.Bu.KU,
        text: "",
        scope: undefined,
        percent: 0,
    };

    /** Pure row predicate - delegates to the shared implementation. */
    static hasExtraTaxToCategory = Model.hasExtraTaxToCategory;

    /** Pure row predicate - delegates to the shared implementation. */
    static isTaxOnTop = Model.isTaxOnTop;

    protected static buTimeframesCache: {
        company?: GQL.ICompany;
        tf: BuTimeframe[];
    } = { tf: [] };

    static setBuTimeframesCacheValue(tf: BuTimeframe[], company: GQL.ICompany) {
        this.buTimeframesCache = { company, tf };
    }

    static dangerouslyGetBuTimeframesData(year: number) {
        const { tf: buTimeframes, company } = this.buTimeframesCache;
        const configs = company?.accountingConfigs || [];
        const config = configs.find((v) => v.year === year);
        return {
            buTimeframes,
            taxation: config?.taxation,
            kontoExt: config?.kontoExt as Model.CompanyKontoExt,
        };
    }

    static getAnzahlungenBuTaxList(
        skr: number,
        year: number,
        period: number,
        buTimeframes: BuTimeframe[] = [],
    ): BuTaxDB[] {
        const items = this.listBuTaxesForYearPeriod(skr, year, period, buTimeframes);
        return items.filter((value) => [Model.Bu.KU, Model.Bu.BU8, Model.Bu.BU9].includes(value.bu));
    }

    static getCategoryBuTaxListYear(
        v: Model.IExtNum,
        skr: number,
        year: number,
        buTimeframes: BuTimeframe[] = [],
    ): BuTaxDB[] {
        const all = this.listBuTaxesForYearPeriod(skr, year, 1, buTimeframes);
        if (v instanceof Category) {
            switch (v.buScope) {
                case Model.BuScope.M:
                    return all.filter((buTax) => buTax.bu === Model.Bu.KU || buTax.scope === Model.BuScope.M);
                case Model.BuScope.V:
                    return all.filter((buTax) => buTax.bu === Model.Bu.KU || buTax.scope === Model.BuScope.V);
                case "AM":
                case "AV":
                case "SAV":
                case "SAM":
                    return all.filter((buTax) => buTax.bu === v.buAuto);
                case "KU":
                    return all.filter((buTax) => buTax.bu === Model.Bu.KU);
            }
        }
        return all;
    }

    protected static EmptyMap = new Map();
    protected static EmptyList: BuTaxDB[] = [];

    static getBuTimeframeCategoriesByYear(
        skr: number,
        year: number,
        buTimeframes: BuTimeframe[],
    ): BuTimeframe["defaultCategories"] {
        const buTimeframe = this.getBuTimeframeYearPeriod(skr, buTimeframes, year, 1);
        return buTimeframe?.defaultCategories || this.EmptyMap;
    }

    static hasBuTimeframeCategoriesByYear(
        skr: number,
        year: number,
        buTimeframes: Pick<BuTimeframe, "skr" | "from" | "to">[],
    ): boolean {
        const buTimeframe = this.getBuTimeframeYearPeriod(skr, buTimeframes as BuTimeframe[], year, 1);
        return Boolean(buTimeframe);
    }

    static getBuTimeframeYearPeriod(
        skr: number,
        buTimeframes: BuTimeframe[],
        year: number,
        period: number,
    ): BuTimeframe | undefined {
        const { month } = Model.getMonthAndDay(period);
        const date = new Date(year, month, 15);
        return buTimeframes.find((tf) => {
            if (tf.skr !== skr) {
                return false;
            }
            return date >= tf.from && date < tf.to;
        });
    }

    static listBuTaxesForYearPeriod(
        skr: number,
        year: number,
        period: number,
        buTimeframes: BuTimeframe[] = [],
    ): BuTaxDB[] {
        if (!buTimeframes?.length) {
            return this.EmptyList;
        }
        const buTimeframe = this.getBuTimeframeYearPeriod(skr, buTimeframes, year, period);
        if (!buTimeframe) {
            if (period !== undefined) {
            }
            return this.EmptyList;
        }

        if (buTimeframe.items.some((v) => v.bu === Model.Bu.KU)) {
            return buTimeframe.items;
        }

        return [this.TAX_BU_KU_DB, ...buTimeframe.items];
    }

    static getBuTaxYearPeriod(
        buToFind: Model.Bu,
        skr: number,
        year: number,
        period: number,
        buTimeframes: BuTimeframe[] = [],
    ): BuTaxDB {
        const items = this.listBuTaxesForYearPeriod(skr, year, period, buTimeframes);
        return items.find((v) => v.bu === buToFind) ?? this.TAX_BU_KU_DB;
    }
}
